import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
// #546: Das Icon heisst FileIcon. Als "File" ueberdeckte es den Browser-Konstruktor,
// und new File(...) beim Einfuegen von Text warf still.
import { Edit3, File as FileIcon, MessageSquare, Mail, CheckCircle, UserPlus, Hourglass, RotateCcw, Sparkles, CheckSquare, Search, Phone, Smartphone, Copy } from 'lucide-react';
import apiService from '../services/apiService';
import EmailDetail from './EmailDetail';
import TeamsMessageDetail from './TeamsMessageDetail';
import TicketChecklist from './TicketChecklist';
import TicketAiSearchModal from './TicketAiSearchModal';
import { CalenderMarkersDisplay, CalenderMarkerHeaderSelect } from '../utils/calenderMarkerUtils';
import { getCustomerLabel, getCustomerOptions, invalidateCustomerCache } from '../utils/customerCache';
import { getContactById } from '../utils/contactCache';
import { erstelleTicketFormat } from '../utils/ticketFormat';
import Combobox from './ui/Combobox';
import Modal from './ui/Modal';
import TicketCustomerField, { telHref } from './TicketCustomerField';
import { useToast } from './ToastProvider';
import { getFeatures } from '../utils/featuresCache';
import { darfTranskribieren } from '../utils/ticketHistorie';
import { useConfirm } from './ui/ConfirmDialog';
import DayCalendarPanel from './DayCalendarPanel';
import UebergabeModal, { useUebergabe } from './ticketDetail/Uebergabe';
import AbschlussModal, { useAbschluss } from './ticketDetail/Abschluss';
import KommentarDialoge, { useKommentare } from './ticketDetail/Kommentare';
import { useTicketHistorie, erstelleHistorienEintrag } from './ticketDetail/Historie';
import AnhangDialoge, { useAnhaenge } from './ticketDetail/Anhaenge';
import { useMailAnhangViewer } from './ticketDetail/MailAnhaenge';
import VorgangDialog from './ticketDetail/VorgangDialog';
import '../styles/TicketDetail.css';
import { useI18n } from '../utils/i18n';

const TicketDetail = ({ ticket, onClose, onTicketUpdate, isMobileView = false, scrollTo = null, hideCalendarSidebar = false }) => {
  const { t, getLocale } = useI18n();
  // #470: Format- und Uebersetzungshelfer (utils/ticketFormat.js) mit
  // gebundener Sprache -- die Aufrufstellen bleiben unveraendert.
  const {
    formatDate, cleanPreviewText, getStatusColor, getPriorityColor,
    translateStatus, translatePriority, formatFileSize
  } = useMemo(() => erstelleTicketFormat({ t, getLocale }), [t, getLocale]);
  const confirm = useConfirm();
  const { pushToast } = useToast();
  const [currentTicket, setCurrentTicket] = useState(ticket);
  // #35: Anzeige-Kurzform des zugeordneten Kunden (null = kein Kunde)
  const [customerLabel, setCustomerLabel] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (currentTicket?.customerId) {
      getCustomerLabel(currentTicket.customerId).then(label => {
        if (!cancelled) setCustomerLabel(label);
      });
    } else {
      setCustomerLabel(null);
    }
    return () => { cancelled = true; };
  }, [currentTicket?.customerId]);
  // #105: zugeordneter Ansprechpartner (Anzeige mit tel:-Links neben dem Kunden)
  const [contactInfo, setContactInfo] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (currentTicket?.customerId && currentTicket?.contactId) {
      getContactById(currentTicket.customerId, currentTicket.contactId).then(info => {
        if (!cancelled) setContactInfo(info);
      });
    } else {
      setContactInfo(null);
    }
    return () => { cancelled = true; };
  }, [currentTicket?.customerId, currentTicket?.contactId]);
  const copyContactNumber = (e, num) => {
    e.preventDefault();
    try {
      navigator.clipboard.writeText(num);
      pushToast({ message: t('customerField.numberCopied', 'Nummer kopiert.'), type: 'success' });
    } catch (_) {}
  };
  // #35 Stufe 2: Kunde direkt in der Detailansicht zuordnen (CustomerSelect)
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerOptions, setCustomerOptions] = useState([]);
  const openCustomerEdit = async () => {
    invalidateCustomerCache();
    const opts = await getCustomerOptions();
    setCustomerOptions(opts);
    setEditingCustomer(true);
  };
  const handleCustomerChange = async (newCustomerId) => {
    try {
      await apiService.updateTicket(currentTicket.id, { customerId: newCustomerId });
      // #105: Der Server setzt bei Kundenwechsel den Ansprechpartner zurück —
      // im lokalen State nachziehen, sonst zeigt die Zeile einen fremden Kontakt.
      setCurrentTicket(prev => ({
        ...prev,
        customerId: newCustomerId,
        contactId: newCustomerId === prev.customerId ? prev.contactId : null
      }));
      setEditingCustomer(false);
    } catch (e) {
      console.error('Kunde zuordnen fehlgeschlagen:', e);
      setEditingCustomer(false);
    }
  };
  // F2/#35: Features nur anbieten, wenn serverseitig aktiv (KI-Zusammenfassung, Kundenfeld, KI-Suche)
  const [summaryAiEnabled, setSummaryAiEnabled] = useState(true);
  const [customersEnabled, setCustomersEnabled] = useState(true);
  const [searchAiEnabled, setSearchAiEnabled] = useState(true);
  // #508: Audio-KI (Transkribieren an Audio-Anhängen); bis zur Antwort aus, der Knopf soll nicht aufblitzen
  const [voicemailAiAn, setVoicemailAiAn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getFeatures().then(features => {
      if (cancelled) return;
      setSummaryAiEnabled(features.summaryAi !== false);
      setCustomersEnabled(features.customers !== false);
      setSearchAiEnabled(features.searchAi !== false);
      setVoicemailAiAn(features.voicemailAi !== false);
    });
    return () => { cancelled = true; };
  }, []);
  // #123: „Ähnliche Vorfälle" (KI-Suche im Ticket)
  const [showAiSearchModal, setShowAiSearchModal] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false); // Track if ticket was modified
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [showEmailDetail, setShowEmailDetail] = useState(false);
  const [selectedTeamsMessage, setSelectedTeamsMessage] = useState(null);
  const [showTeamsMessageDetail, setShowTeamsMessageDetail] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    title: '',
    description: '',
    status: '',
    priority: '',
    category: '',
    assignedTo: '',
    calenderMarker: null
  });
  const [updating, setUpdating] = useState(false);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  // #530 (Scheibe 2 aus #310): Abschluss mit Vorgangs-Rueckfrage und
  // Folgeticket (components/ticketDetail/Abschluss.js). Steht hinter
  // currentUser, weil der Hook es beim Rendern bekommt.
  const abschluss = useAbschluss({ ticket, currentTicket, setCurrentTicket, currentUser, onClose, setError, setHasChanges });
  const { showCompleteModal, completing, handleOpenCompleteModal } = abschluss;
  // #530 (Scheibe 1 aus #310): Uebergabe samt Planungsfragen
  // (components/ticketDetail/Uebergabe.js). getCurrentUserEmail kommt als
  // Pfeil herein, weil es weiter unten deklariert ist.
  const uebergabe = useUebergabe({ ticket, currentTicket, onClose, onTicketUpdate, setError, setHasChanges, getCurrentUserEmail: () => getCurrentUserEmail() });
  const { showTransferModal, transferring, handleOpenTransferModal } = uebergabe;
  
  // #474: Die Historie haengt in zwei Zweigen (mit Checkliste im geteilten
  // Layout und ohne) und wird beim Umschalten neu eingehaengt -- ebenso nach
  // "Zur Historie" aus der KI-Zusammenfassung und wenn sich die Checkliste
  // beim Laden von selbst oeffnet. Das Element steht deshalb im State (der
  // Setter ist der Callback-Ref), damit Scroll-Listener und Thumbnail-Observer
  // ihm folgen. Mit einem useRef blieben beide am alten Element haengen, bis
  // sich die Historie aenderte.
  // #530 (Scheibe 4 aus #310): Historie laden, beim Scrollen nachladen und
  // zum Ziel springen (components/ticketDetail/Historie.js). Steht hinter
  // setError, weil der Hook es beim Rendern bekommt. Der Init-Effekt, der
  // loadTicketHistory beim Wechsel der Ticket-id aufruft, bleibt hier.
  const {
    history, loading, historyListEl, setHistoryListEl, visibleHistoryCount, attachmentsLoading, loadTicketHistory
  } = useTicketHistorie({ ticket, setError, scrollTo });
  // #231c: Treffer aus „Ähnliche Vorfälle" on-top öffnen statt das Overlay zu ersetzen —
  // das Ziel-Ticket wird lokal gehalten und als zweite TicketDetail-Instanz über dem
  // KI-Modal gerendert; nach dem Schließen ist die Ähnlichkeitssuche wieder da.
  const [stackedTicket, setStackedTicket] = useState(null);
  const [stackedLoading, setStackedLoading] = useState(false);
  const openStackedTicket = async (ticketId) => {
    if (!ticketId || stackedLoading) return;
    setStackedLoading(true);
    try {
      try { await apiService.markTicketAsViewed(ticketId); } catch (_) {}
      const resp = await apiService.getTicket(ticketId);
      const target = resp?.ticket || resp;
      if (target && target.id != null) setStackedTicket(target);
    } catch (_) {
    } finally {
      setStackedLoading(false);
    }
  };
  // #530 (Scheibe 3 aus #310): Kommentare, KI-Zusammenfassung und
  // KI-Beschreibung (components/ticketDetail/Kommentare.js).
  // loadTicketHistory kommt als Pfeil herein, weil es weiter unten steht.
  const kommentare = useKommentare({ ticket, currentTicket, setCurrentTicket, onTicketUpdate, setError, setHasChanges, loadTicketHistory: () => loadTicketHistory() });
  const {
    showAddCommentModal, setShowAddCommentModal, showEditCommentModal, aiSummarizing, aiSummary, aiError,
    descSummarizing, handleOpenEditCommentModal, handleGenerateAiSummary, handleClearAiSummary, handleSummarizeDescription
  } = kommentare;

  // #530 (Scheibe 5 aus #310): Anhaenge -- hochladen, einfuegen, ziehen,
  // Vorschaubilder, Bildansicht, herunterladen, loeschen
  // (components/ticketDetail/Anhaenge.js). Steht hinter den Dialog-Hooks,
  // weil Paste- und Drag-Sperre deren Zustand beim Rendern bekommen.
  const anhaenge = useAnhaenge({
    ticket, currentTicket, history, historyListEl, visibleHistoryCount, setError, setHasChanges, loadTicketHistory,
    showAddCommentModal, showEditModal, showEditCommentModal, showTransferModal, showCompleteModal
  });
  // #636: Bilder und PDFs aus Mail-Anhaengen (#635) im AnhangViewer
  const mailAnhangViewer = useMailAnhangViewer();
  const {
    setShowAttachmentModal, setAttachmentError, thumbnailUrls, isDragOver, handlePasteFromClipboardEvent, handleImageClick,
    handleDownloadAttachment, handleDeleteAttachment, handleDragEnter, handleDragOver, handleDragLeave, handleDrop
  } = anhaenge;

  // Checklist state
  const [showChecklist, setShowChecklist] = useState(false);
  const [checklistItems, setChecklistItems] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);

  useEffect(() => {
    setCurrentTicket(ticket);
    loadTicketHistory();
    loadAvailableEmails();
    // Checkliste laden und automatisch öffnen wenn Items vorhanden
    (async () => {
      try {
        setChecklistLoading(true);
        const resp = await apiService.getTicketChecklist(ticket.id);
        const items = resp.items || [];
        setChecklistItems(items);
        if (items.length > 0) {
          setShowChecklist(true);
        }
      } catch (error) {
        console.error('Error auto-loading ticket checklist:', error);
      } finally {
        setChecklistLoading(false);
      }
    })();
  }, [ticket.id]);

  // Update currentTicket when ticket prop changes
  useEffect(() => {
    setCurrentTicket(ticket);
  }, [ticket]);

  // Load current user once
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const userResponse = await apiService.getCurrentUser();
        setCurrentUser(userResponse);
      } catch (err) {
        console.error('Error loading current user:', err);
      }
    };
    loadCurrentUser();
  }, []);

  const loadAvailableEmails = async () => {
    try {
      const response = await apiService.getAvailableEmails();
      setAvailableEmails(response.emails || []);
    } catch (error) {
      console.error('Error loading available emails:', error);
    }
  };

  const getCurrentUserEmail = () => {
    return currentUser?.mail || currentUser?.userPrincipalName || currentUser?.email || '';
  };

  const loadTicketChecklist = async () => {
    try {
      setChecklistLoading(true);
      const resp = await apiService.getTicketChecklist(ticket.id);
      setChecklistItems(resp.items || []);
    } catch (error) {
      console.error('Error loading ticket checklist:', error);
    } finally {
      setChecklistLoading(false);
    }
  };

  const handleAddChecklistItem = async (content) => {
    try {
      const resp = await apiService.createTicketChecklistItem(ticket.id, content);
      if (resp.item) {
        setChecklistItems(prev => [...prev, resp.item]);
      }
    } catch (error) {
      console.error('Error adding checklist item:', error);
    }
  };

  const handleToggleChecklistItem = async (itemId, isCompleted) => {
    try {
      await apiService.updateTicketChecklistItem(ticket.id, itemId, { isCompleted });
      setChecklistItems(prev =>
        prev.map(item =>
          item.id === itemId ? { ...item, isCompleted } : item
        )
      );
    } catch (error) {
      console.error('Error toggling checklist item:', error);
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    try {
      await apiService.deleteTicketChecklistItem(ticket.id, itemId);
      setChecklistItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting checklist item:', error);
    }
  };

  const handleReorderChecklistItems = async (orderedIds) => {
    try {
      await apiService.reorderTicketChecklist(ticket.id, orderedIds);
      // Lokale Reihenfolge anhand der neuen IDs aktualisieren
      const idMap = new Map(checklistItems.map(i => [i.id, i]));
      setChecklistItems(orderedIds.map(id => idMap.get(id)).filter(Boolean));
    } catch (error) {
      console.error('Error reordering checklist items:', error);
    }
  };

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
    setShowEmailDetail(true);
  };

  const handleCloseEmailDetail = () => {
    setShowEmailDetail(false);
    setSelectedEmail(null);
  };

  const handleTeamsMessageClick = (teamsMessage) => {
    setSelectedTeamsMessage(teamsMessage);
    setShowTeamsMessageDetail(true);
  };

  const handleCloseTeamsMessageDetail = () => {
    setShowTeamsMessageDetail(false);
    setSelectedTeamsMessage(null);
  };

  // ================ EMAIL CONVERSATION HANDLER ================

  // Handle creating a new email conversation for this ticket.
  // #448: Der Knopf steht immer da, nicht nur solange das Ticket noch keine
  // Mail hat. Das An-Feld wird mit dem Ansprechpartner des Tickets
  // vorbelegt -- aus dem Cache nachgeschlagen statt aus contactInfo, damit
  // es nicht davon abhaengt, ob die Anzeige schon geladen hat. Inaktive
  // Ansprechpartner und Tickets ohne Kunden (atlas: Kundenstamm aus) lassen
  // das Feld leer. Der Entwurf entsteht wie bisher im ueberwachten Postfach.
  const handleNewEmailConversation = async () => {
    try {
      setError(null);
      console.log(`📧 [NEW EMAIL CONVERSATION] Starting new email conversation for ticket ${currentTicket.ticketNumber}`);

      let toEmail = null;
      if (currentTicket?.customerId && currentTicket?.contactId) {
        // Wirft nicht: Der Cache liefert bei Netzfehlern den alten Stand oder null.
        const kontakt = await getContactById(currentTicket.customerId, currentTicket.contactId);
        if (kontakt && kontakt.isActive !== false && kontakt.email) toEmail = kontakt.email;
      }

      // Create a new email draft with the ticket number in the subject
      const subjectTag = await apiService.buildTicketSubjectTag(currentTicket.ticketNumber);
      const response = await apiService.createNewEmailConversation({
        ticketNumber: currentTicket.ticketNumber,
        subject: `${currentTicket.title} ${subjectTag}`.trim(),
        ...(toEmail ? { toEmail } : {})
      });
      
      if (response.success && response.webLink) {
        // Convert /owa/ URL to /mail/deeplink/compose/ URL to open in edit mode (same as reply functionality)
        let editModeLink = response.webLink;
        
        if (response.webLink.includes('/owa/') && response.webLink.includes('ItemID=')) {
          const url = new URL(response.webLink);
          const itemId = url.searchParams.get('ItemID');
          const baseUrl = url.origin;
          
          editModeLink = `${baseUrl}/mail/deeplink/compose/${encodeURIComponent(itemId)}?ItemID=${encodeURIComponent(itemId)}&exvsurl=1`;
        }
        
        // Open the email draft in a new window
        window.open(
          editModeLink, 
          'outlook_new_conversation', 
          'width=1200,height=800,scrollbars=yes,resizable=yes,toolbar=no,location=yes,directories=no,status=no,menubar=no,left=100,top=100'
        );
        
        console.log(`📧 [NEW EMAIL CONVERSATION] Email draft opened for ticket ${currentTicket.ticketNumber}`);
      } else {
        setError(t('ticketDetail.newEmailError', 'Der Mail-Entwurf konnte nicht angelegt werden. Bitte erneut versuchen.'));
      }
    } catch (error) {
      console.error('Error creating new email conversation:', error);
      setError(t('ticketDetail.newEmailError', 'Der Mail-Entwurf konnte nicht angelegt werden. Bitte erneut versuchen.'));
    }
  };

  // #447: Eine von Hand zugeordnete Mail wieder vom Ticket lösen. Ob der Knopf
  // erscheint, hat der Server entschieden (item.loesbar, Regel in
  // server/utils/mailZuordnung.js); die Route prüft Regel und Schreibrecht
  // trotzdem noch einmal. Im Vorgang geht das Lösen gegen das Ticket, an dem
  // die Mail hängt (item.ticketId), nicht zwingend gegen das geöffnete.
  const handleDetachEmail = async (item) => {
    const emailId = item?.email?.id;
    if (!emailId) return;
    const ok = await confirm({
      title: t('ticketDetail.detachEmailTitle', 'Mail vom Ticket lösen'),
      message: t('ticketDetail.detachEmailConfirm', 'Die Mail „{{subject}}“ vom Ticket lösen? In Outlook bleibt sie unverändert, nur die Verknüpfung mit dem Ticket entfällt.', { subject: item.email.subject || '' }),
      confirmLabel: t('ticketDetail.detachEmail', 'Vom Ticket lösen'),
      danger: true
    });
    if (!ok) return;
    try {
      await apiService.removeEmailFromTicket(item.ticketId || currentTicket.id, emailId);
      pushToast({ message: t('ticketDetail.detachEmailDone', 'Mail vom Ticket gelöst.'), type: 'success' });
      setHasChanges(true);
      await loadTicketHistory();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        pushToast({ message: t('ticketDetail.detachEmailGone', 'Die Mail hängt nicht mehr an diesem Ticket.'), type: 'info' });
        await loadTicketHistory();
      } else if (status === 409) {
        pushToast({ message: t('ticketDetail.detachEmailAutomatic', 'Diese Mail wurde automatisch zugeordnet und lässt sich nicht lösen.'), type: 'error' });
      } else if (status === 403) {
        pushToast({ message: t('ticketDetail.detachEmailForbidden', 'Keine Berechtigung, dieses Ticket zu ändern.'), type: 'error' });
      } else {
        console.error('Error detaching email from ticket:', err);
        pushToast({ message: t('ticketDetail.detachEmailError', 'Die Mail konnte nicht gelöst werden.'), type: 'error' });
      }
    }
  };

  const handleEditTicket = () => {
    setEditFormData({
      title: currentTicket.title || '',
      description: currentTicket.description || '',
      status: currentTicket.status || '',
      priority: currentTicket.priority || '',
      category: currentTicket.category || '',
      assignedTo: currentTicket.assignedTo || '',
      calenderMarker: currentTicket.calenderMarker || null,
      customerId: currentTicket.customerId || null,
      contactId: currentTicket.contactId || null
    });
    setShowEditModal(true);
  };

  const handleSaveTicket = async (e) => {
    e.preventDefault();
    if (!editFormData.title.trim()) {
      pushToast({ message: t('dashboard.enterTitle', 'Bitte geben Sie einen Titel ein.'), type: 'error' });
      return;
    }

    try {
      setUpdating(true);
      
      // Check if status is changing from "abgeschlossen" to another status
      const wasCompleted = currentTicket.status === 'abgeschlossen';
      const isNowNotCompleted = editFormData.status !== 'abgeschlossen';
      const statusChangedFromCompleted = wasCompleted && isNowNotCompleted;

      const isNowCompleted = editFormData.status === 'abgeschlossen';
      const currentUserEmail = currentUser?.mail || currentUser?.userPrincipalName || '';
      const assignedToValue = isNowCompleted && currentUserEmail
        ? currentUserEmail
        : editFormData.assignedTo;
      
      // Basis-Ticket-Daten speichern
      const ticketData = {
        title: editFormData.title,
        description: editFormData.description,
        status: editFormData.status,
        priority: editFormData.priority,
        category: editFormData.category,
        assignedTo: assignedToValue,
        calenderMarker: editFormData.calenderMarker,
        customerId: editFormData.customerId || null,
        contactId: editFormData.contactId || null
      };

      const updateResponse = await apiService.updateTicket(ticket.id, ticketData);
      const updated = updateResponse?.ticket || { ...currentTicket, ...ticketData };
      // Inform parent so listings update immediately without reload
      if (onTicketUpdate) {
        try { onTicketUpdate(updated); } catch (_) {}
      }
      
      setShowEditModal(false);
      
      // Mark changes with special flag for status change from completed
      if (statusChangedFromCompleted) {
        setHasChanges('status_uncompleted'); // Special flag for status change from completed
        console.log('🎫 [TICKET DETAIL] Ticket status changed from abgeschlossen to', editFormData.status);
      } else {
        setHasChanges(true); // Regular change
      }
      
      // #543: Die Anzeige kommt aus der Antwort von updateTicket. Vorher wurde die
      // ganze Ticketliste geladen und das Ticket darin gesucht. Fehlte es dort
      // (an jemand anderen abgegeben, abgeschlossen und damit archiviert), blieb
      // die Anzeige alt. Kunde und Ansprechpartner laedt die Anzeige ueber die IDs.
      setCurrentTicket(prev => ({ ...prev, ...updated }));
    } catch (error) {
      if (error?.abgebrochen) return; // #554: Rueckfrage abgelehnt, nichts gespeichert
      console.error('Error updating ticket:', error);
      pushToast({ message: t('ticketDetail.ticketUpdateError', 'Fehler beim Aktualisieren des Tickets.'), type: 'error' });
    } finally {
      setUpdating(false);
    }
  };


  const handleMarkWaiting = async () => {
    try {
      setUpdating(true);
      const updateResponse = await apiService.updateTicket(ticket.id, { status: 'wartend' });
      const updated = updateResponse?.ticket || { ...currentTicket, status: 'wartend' };
      if (onTicketUpdate) {
        try { onTicketUpdate(updated); } catch (_) {}
      }
      setHasChanges('status_waiting');
      onClose('status_waiting');
    } catch (err) {
      console.error('Error marking ticket as waiting:', err);
      setError(t('ticketDetail.waitingError', 'Fehler beim Setzen auf Wartend'));
    } finally {
      setUpdating(false);
    }
  };

  const handleReopenTicket = async () => {
    try {
      setUpdating(true);
      const updateResponse = await apiService.updateTicket(ticket.id, { status: 'offen' });
      const updated = updateResponse?.ticket || { ...currentTicket, status: 'offen' };
      if (onTicketUpdate) {
        try { onTicketUpdate(updated); } catch (_) {}
      }
      // Gleiche Reason wie bei Status-Wechsel von abgeschlossen im Edit-Flow, damit Re-Listing/Unarchive-Flow greift
      setHasChanges('status_uncompleted');
      onClose('status_uncompleted');
    } catch (err) {
      console.error('Error reopening ticket:', err);
      try {
        const status = err?.response?.status;
        if (status === 409) {
          setError(t('ticketDetail.reopenBlocked', 'Wiederöffnen blockiert: Im Vorgang existieren noch offene Tickets oder Zeitblöcke.'));
        } else if (status === 404) {
          setError(t('ticketDetail.notFound', 'Ticket nicht gefunden.'));
        } else {
          setError(t('ticketDetail.reopenError', 'Fehler beim Wiederöffnen des Tickets'));
        }
      } catch (_) {
        setError(t('ticketDetail.reopenError', 'Fehler beim Wiederöffnen des Tickets'));
      }
    } finally {
      setUpdating(false);
    }
  };





  // #602: „Zum Vorgang“ öffnet den Vorgang im Dialog über dem Ticket, statt in
  // die Vorgangs-Ansicht zu wechseln (dort fehlen automatisch angelegte).
  const [showVorgangDialog, setShowVorgangDialog] = useState(false);
  const handleGoToProject = () => setShowVorgangDialog(true);
  const nachVorgangGespeichert = async () => {
    // Der Dialog benennt Tickets mit um (Präfix aus dem Vorgangsnamen)
    try {
      const resp = await apiService.getTicket(currentTicket.id);
      const neu = resp?.ticket || resp;
      if (neu && neu.id != null) setCurrentTicket(neu);
    } catch (_) {
      // Anzeige bleibt beim alten Stand, die Liste zieht über onTicketUpdate nach
    }
    try { onTicketUpdate && onTicketUpdate(); } catch (_) { /* Aufrufer-Fehler nicht ins Speichern tragen */ }
    loadTicketHistory();
  };

  const isProjectTicket = !!(currentTicket?.projectId || currentTicket?.subprojectId);
  // #530 (Scheibe 4): Ein Historien-Eintrag (components/ticketDetail/Historie.js). Wie
  // vorher bei jedem Rendern neu gebunden; steht hinter isProjectTicket, das er braucht.
  // #508: „Transkribieren“ an Audio-Anhängen; danach den Verlauf neu laden,
  // damit der interne Kommentar mit dem Transkript erscheint.
  const transkribiere = darfTranskribieren({ featureAn: voicemailAiAn, ticket: currentTicket, dbUser: currentUser?.dbUser })
    ? async ({ quelle, ticketId, anhangId, emailId }) => {
      await apiService.transkribiereAnhang(ticketId, { quelle, anhangId, emailId });
      await loadTicketHistory();
    }
    : undefined;
  const renderHistoryItem = erstelleHistorienEintrag({
    oeffneMailAnhang: mailAnhangViewer.oeffnenFuer,
    transkribiere,
    t, history, currentTicket, isProjectTicket, getCurrentUserEmail, formatDate, formatFileSize, cleanPreviewText, thumbnailUrls,
    handleOpenEditCommentModal, handleEmailClick, handleTeamsMessageClick, handleDetachEmail, handleImageClick, handleDownloadAttachment, handleDeleteAttachment
  });

  const showDayCalendarPanel = useMemo(() => {
    try {
      if (hideCalendarSidebar) { return false; }
      const assigned = (currentTicket?.assignedTo || '').toLowerCase();
      const me = (getCurrentUserEmail() || '').toLowerCase();
      const isAssignedToMe = !!me && assigned === me;
      const isPlanned = !!(currentTicket?.plannedStartDateTime || currentTicket?.isPlanned);
      return isAssignedToMe && isPlanned;
    } catch (_) { return false; }
  }, [currentTicket?.assignedTo, currentTicket?.plannedStartDateTime, currentTicket?.isPlanned, currentUser, hideCalendarSidebar]);

  const plannedDay = useMemo(() => {
    try {
      return currentTicket?.plannedStartDateTime ? new Date(currentTicket.plannedStartDateTime) : null;
    } catch (_) { return null; }
  }, [currentTicket?.plannedStartDateTime]);

  return (
    <div 
      className="ticket-detail-overlay"
      onPaste={handlePasteFromClipboardEvent}
      onDragEnter={(e) => { try { if (e.dataTransfer.types && e.dataTransfer.types.includes('text/plain')) return; } catch(_) {} handleDragEnter(e); }}
      onDragOver={(e) => { try { if (e.dataTransfer.types && e.dataTransfer.types.includes('text/plain')) return; } catch(_) {} handleDragOver(e); }}
      onDragLeave={(e) => { try { if (e.dataTransfer.types && e.dataTransfer.types.includes('text/plain')) return; } catch(_) {} handleDragLeave(e); }}
      onDrop={(e) => { try { if (e.dataTransfer.types && e.dataTransfer.types.includes('text/plain')) return; } catch(_) {} handleDrop(e); }}
    >
      {/* Höhe kommt aus TicketDetail.css (.ticket-detail-flex-wrap, 100dvh-Fallback, #16 A1) */}
      <div className="ticket-detail-flex-wrap" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 16 }}>
        {showDayCalendarPanel && plannedDay && !isMobileView && (
          <DayCalendarPanel 
            date={plannedDay}
            currentUser={currentUser}
            highlightTicketId={currentTicket?.id}
            highlightTicketNumber={currentTicket?.ticketNumber}
          />
        )}
        <div className="ticket-detail-container">
        <div className={`ticket-detail-header${isMobileView ? ' mobile-compact' : ''}`}>
          {/* W6 (F2): Ticketnummer auch mobil anzeigen — mobile-compact macht sie nur kleiner */}
          <h2>{isProjectTicket ? t('ticketDetail.block', 'Zeitblock') : t('ticketDetail.ticket', 'Ticket')} #{currentTicket.ticketNumber}</h2>
          <div className="header-actions">
            {!isMobileView && (currentTicket?.projectId || currentTicket?.subprojectId) && (
              <button 
                className="project-button"
                onClick={handleGoToProject}
                disabled={updating}
                title={t('ticketDetail.toProject', 'Zum Vorgang wechseln')}
              >
                {t('ticketDetail.toProject', 'Zum Vorgang')}
              </button>
            )}
            
            {currentTicket.status !== 'abgeschlossen' && (
              <button
                className="transfer-button"
                onClick={handleOpenTransferModal}
                disabled={updating || transferring}
                title={t('ticketDetail.transferTitle', 'Ticket übergeben')}
              >
                <UserPlus size={16} />
                {t('ticketDetail.transfer', 'Übergeben')}
              </button>
            )}
            {/* #39: Abschließen ist bewusst auch bei fremd zugewiesenen Tickets
                möglich; das Complete-Modal weist dann auf den Zugewiesenen hin
                und der Server protokolliert closedBy. */}
            {currentTicket.status !== 'abgeschlossen' && (
              <button
                className="complete-button"
                onClick={handleOpenCompleteModal}
                disabled={updating || completing}
                title={t('ticketDetail.completeTitle', 'Ticket abschließen')}
              >
                <CheckCircle size={16} />
                {t('ticketDetail.complete', 'Abschließen')}
              </button>
            )}
            {/* W6 (F3): Reopen/Wartend/Bearbeiten waren mobil ausgeblendet —
                ein Techniker muss den Status auch unterwegs setzen können.
                Die Leiste bricht mobil um (CSS), statt Funktionen zu verstecken. */}
            {(currentTicket.status === 'abgeschlossen' || currentTicket.status === 'wartend') && (
              <button
                className="reopen-button"
                onClick={handleReopenTicket}
                disabled={updating}
                title={t('ticketDetail.reopenTitle', 'Ticket wieder öffnen')} aria-label={t('ticketDetail.reopenTitle', 'Ticket wieder öffnen')}
              >
                <RotateCcw size={16} />
              </button>
            )}
            {currentTicket.status !== 'wartend' && currentTicket.status !== 'abgeschlossen' && (
              <button
                className="waiting-button"
                onClick={handleMarkWaiting}
                disabled={updating}
                title={t('ticketDetail.markWaiting', 'Als wartend markieren')} aria-label={t('ticketDetail.markWaiting', 'Als wartend markieren')}
              >
                <Hourglass size={16} />
              </button>
            )}
            <button
              className="edit-button"
              onClick={handleEditTicket}
              disabled={updating}
              title={t('ticketDetail.edit', 'Bearbeiten')} aria-label={t('ticketDetail.edit', 'Bearbeiten')}
            >
              <Edit3 size={16} />
            </button>
            <button className="close-button" onClick={() => onClose(hasChanges)}>×</button>
          </div>
        </div>

        <div className="ticket-info">
          <div className="ticket-main-content">
            <div className="ticket-meta-sidebar">
              {/* Spalte 1: Status, Priorität, Geplant für */}
              <div className="meta-column">
                <div className="meta-item">
                  <span className="meta-label">{t('ticketDetail.status', 'Status')}</span>
                  <span className="meta-value">
                    <span 
                      className="status-badge" 
                      style={{ backgroundColor: getStatusColor(currentTicket.status) }}
                    >
                      {translateStatus(currentTicket.status)}
                    </span>
                  </span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">{t('ticketDetail.priority', 'Priorität')}</span>
                  <span className="meta-value">
                    <span 
                      className="priority-badge" 
                      style={{ backgroundColor: getPriorityColor(currentTicket.priority) }}
                    >
                      {translatePriority(currentTicket.priority)}
                    </span>
                  </span>
                </div>
                {currentTicket.isPlanned && !isMobileView && (
                  <div className="meta-item">
                    <span className="meta-label">{t('ticketDetail.plannedFor', 'Geplant für')}</span>
                    <span className="meta-value">
                      {new Date(currentTicket.plannedStartDateTime).toLocaleDateString(getLocale(), {
                        weekday: 'short',
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Spalte 2: Erstellt, Erstellt Von, Zeitraum */}
              <div className="meta-column">
                {!isMobileView && (
                  <div className="meta-item">
                    <span className="meta-label">{t('ticketDetail.created', 'Erstellt')}</span>
                    <span className="meta-value">{formatDate(currentTicket.createdAt)}</span>
                  </div>
                )}
                {currentTicket.createdBy && !isMobileView && (
                  <div className="meta-item">
                    <span className="meta-label">{t('ticketDetail.createdBy', 'Erstellt von')}</span>
                    <span className="meta-value">{currentTicket.createdBy}</span>
                  </div>
                )}
                {currentTicket.status === 'abgeschlossen' && currentTicket.closedBy && (
                  <div className="meta-item">
                    <span className="meta-label">{t('ticketDetail.closedBy', 'Abgeschlossen von')}</span>
                    <span className="meta-value">{currentTicket.closedBy}</span>
                  </div>
                )}
                {currentTicket.isPlanned && (
                  <div className="meta-item">
                    <span className="meta-label">{t('ticketDetail.period', 'Zeitraum')}</span>
                    <span className="meta-value">
                      {new Date(currentTicket.plannedStartDateTime).toLocaleTimeString(getLocale(), {
                        hour: '2-digit',
                        minute: '2-digit'
                      })} - {new Date(currentTicket.plannedEndDateTime).toLocaleTimeString(getLocale(), {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}

                {/* Meeting Join Button (Teams) */}
                {(() => {
                  try {
                    const desc = String(currentTicket.description || '');
                    const m = desc.match(/https?:\/\/(?:\S*?\.)?teams\.microsoft\.com\/\S*/i);
                    const joinUrl = m ? m[0] : null;
                    if (!joinUrl) return null;
                    return (
                      <div className="meta-item">
                        <span className="meta-label">{t('ticketDetail.meeting', 'Meeting')}</span>
                        <span className="meta-value">
                          <a
                            href={joinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-primary btn-sm"
                          >
                            {t('ticketDetail.joinMeeting', 'Meeting beitreten')}
                          </a>
                        </span>
                      </div>
                    );
                  } catch (_) { return null; }
                })()}
              </div>

              {/* Spalte 3: Leer oder für zukünftige Erweiterungen */}
              <div className="meta-column">
                {/* Platzhalter */}
              </div>
            </div>

            <div className="ticket-content-area">
              {/* #89: Kunde prominent über dem Titel (statt in der Meta-Spalte) */}
              {customersEnabled && (
                <div className="ticket-customer-line">
                  {editingCustomer ? (
                    <span style={{ minWidth: 260, display: 'inline-block' }}>
                      <Combobox
                        options={customerOptions}
                        value={currentTicket.customerId || null}
                        onChange={handleCustomerChange}
                        allowClear
                        clearLabel={t('ticketDetail.customerNone', '— kein Kunde —')}
                        noResultsText={t('ui.noResults', 'Keine Treffer')}
                        placeholder={t('ticketDetail.customerSelect', 'Kunde wählen…')}
                      />
                    </span>
                  ) : (
                    <>
                      <span className={`ticket-customer-name${customerLabel ? '' : ' is-empty'}`}>
                        {customerLabel || t('ticketDetail.customerNone', '— kein Kunde —')}
                      </span>
                      <button
                        type="button"
                        onClick={openCustomerEdit}
                        title={t('ticketDetail.customerEdit', 'Kunde zuordnen')} aria-label={t('ticketDetail.customerEdit', 'Kunde zuordnen')}
                        style={{ marginLeft: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-muted)', padding: 0, verticalAlign: 'middle' }}
                      >
                        <Edit3 size={14} />
                      </button>
                      {/* #105: Ansprechpartner mit Nummern — Klick ruft an (tel:),
                          Rechtsklick oder Kopier-Knopf legt die Nummer in die Zwischenablage */}
                      {contactInfo && (
                        <span className="ticket-contact-info">
                          <span className="ticket-contact-name">{contactInfo.name}</span>
                          {[
                            { num: contactInfo.phoneMobile, Icon: Smartphone },
                            { num: contactInfo.phoneLandline, Icon: Phone }
                          ].filter(x => x.num).map(({ num, Icon }) => (
                            <span key={num} className="ticket-contact-phone">
                              <a
                                href={telHref(num)}
                                title={t('customerField.callTitle', 'Anrufen — Rechtsklick kopiert die Nummer')}
                                onContextMenu={(e) => copyContactNumber(e, num)}
                              >
                                <Icon size={13} />
                                {num}
                              </a>
                              <button
                                type="button"
                                onClick={(e) => copyContactNumber(e, num)}
                                title={t('customerField.copyNumber', 'Nummer kopieren')} aria-label={t('customerField.copyNumber', 'Nummer kopieren')}
                              >
                                <Copy size={12} />
                              </button>
                            </span>
                          ))}
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
              <div className="ticket-subject" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CalenderMarkersDisplay markers={currentTicket.calenderMarker} size={20} />
                <h3>{currentTicket.title}</h3>
              </div>

              {currentTicket.description && (
                <div className="description-content" style={{ position: 'relative', paddingRight: summaryAiEnabled && !currentTicket.isArchived ? 34 : undefined }}>
                  {/* #208: lange Beschreibung per LLM zusammenfassen (Vorschlag mit Bestätigung) */}
                  {summaryAiEnabled && !currentTicket.isArchived && (
                    <button
                      type="button"
                      onClick={handleSummarizeDescription}
                      className="add-ai-summary-btn-small"
                      style={{ position: 'absolute', top: 4, right: 4 }}
                      title={t('ticketDetail.descSummarize', 'Beschreibung per KI zusammenfassen')} aria-label={t('ticketDetail.descSummarize', 'Beschreibung per KI zusammenfassen')}
                      disabled={descSummarizing}
                    >
                      <Sparkles size={14} />
                    </button>
                  )}
                  {currentTicket.description}
            </div>
          )}
        </div>
            </div>
        </div>

        <div className={`ticket-history${aiSummarizing ? ' ai-summarizing' : ''}${showChecklist ? ' checklist-split' : ''}`}>
          <div className="history-header-section">
            <button
              onClick={() => {
                const next = !showChecklist;
                setShowChecklist(next);
                if (next) loadTicketChecklist();
              }}
              className="checklist-btn-small"
              title={showChecklist ? 'Checkliste ausblenden' : 'Checkliste'} aria-label={showChecklist ? 'Checkliste ausblenden' : 'Checkliste'}
              style={{
                background: showChecklist ? 'var(--color-accent-2)' : 'transparent',
                color: showChecklist ? 'var(--color-text-inverse)' : 'var(--color-text-light)',
                border: '1px solid',
                borderColor: showChecklist ? 'var(--color-accent-2)' : 'var(--color-border-neutral)',
                borderRadius: 8,
                padding: '6px 8px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckSquare size={16} />
            </button>
            <h3>{t('ticketDetail.history', 'Historie')}</h3>
            <div className="history-actions">
              {attachmentsLoading && (
                <div className="dots-loading" title={t('ticketDetail.loadingAttachments', 'Lade Anhänge...')} aria-label={t('ticketDetail.loadingAttachments', 'Lade Anhänge...')}>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </div>
              )}
              {summaryAiEnabled && (
                <button
                  onClick={aiSummary ? handleClearAiSummary : handleGenerateAiSummary}
                  className="add-ai-summary-btn-small"
                  title={aiSummary ? t('ticketDetail.backToHistory', 'Zur Historie zurück') : t('ticketDetail.aiSummary', 'KI-Zusammenfassung')} aria-label={aiSummary ? t('ticketDetail.backToHistory', 'Zur Historie zurück') : t('ticketDetail.aiSummary', 'KI-Zusammenfassung')}
                  disabled={aiSummarizing}
                >
                  <Sparkles size={16} />
                </button>
              )}
              {searchAiEnabled && (
                <button
                  onClick={() => setShowAiSearchModal(true)}
                  className="add-ai-summary-btn-small"
                  title={t('ticketDetail.aiSimilar', 'Ähnliche Vorfälle (KI-Suche)')} aria-label={t('ticketDetail.aiSimilar', 'Ähnliche Vorfälle (KI-Suche)')}
                >
                  <Search size={16} />
                </button>
              )}
              <button
                onClick={() => { setAttachmentError(null); setShowAttachmentModal(true); }}
                className="add-attachment-btn-small"
                title={t('ticketDetail.attach', 'Dateien anhängen')} aria-label={t('ticketDetail.attach', 'Dateien anhängen')}
              >
                <FileIcon size={16} />
              </button>
              <button
                onClick={handleNewEmailConversation}
                className="add-email-conversation-btn-small"
                title={t('ticketDetail.newEmail', 'Neue Mail-Konversation')} aria-label={t('ticketDetail.newEmail', 'Neue Mail-Konversation')}
              >
                <Mail size={16} />
              </button>
              <button
                onClick={() => setShowAddCommentModal(true)}
                className="add-comment-btn-small"
                title={t('ticketDetail.addComment', 'Kommentar hinzufügen')} aria-label={t('ticketDetail.addComment', 'Kommentar hinzufügen')}
              >
                <MessageSquare size={16} />
              </button>
            </div>
          </div>

          {showChecklist ? (
            <div className="history-split-layout">
              <div className="history-split-left">
                <TicketChecklist
                  items={checklistItems}
                  loading={checklistLoading}
                  onAddItem={handleAddChecklistItem}
                  onToggleItem={handleToggleChecklistItem}
                  onDeleteItem={handleDeleteChecklistItem}
                  onReorderItems={handleReorderChecklistItems}
                />
              </div>
              <div className="history-split-right">
                {error && <div className="error-message">{error}</div>}
                {aiError && <div className="error-message">{aiError}</div>}
                {loading ? (
                  <div className="loading">{t('ticketDetail.loadingHistory', 'Lade Historie...')}</div>
                ) : aiSummary ? (
                  <div className="ai-summary-view">
                    <div className="ai-summary-header">
                      <strong>{t('ticketDetail.aiSummary', 'KI-Zusammenfassung')}</strong>
                      <button className="btn btn-secondary btn-sm" onClick={handleClearAiSummary} disabled={aiSummarizing} style={{ marginLeft: 8 }}>
                        {t('ticketDetail.backToHistory', 'Zur Historie')}
                      </button>
                    </div>
                    <div className="ai-summary-content">
                      <ReactMarkdown>{aiSummary}</ReactMarkdown>
                    </div>
                  </div>
                ) : (
                  <div className="history-list" ref={setHistoryListEl}>
                    {(() => {
                      const filteredHistory = history.filter(item => item.type !== 'system');
                      const sliced = filteredHistory.slice(0, visibleHistoryCount);
                      return sliced.length === 0 ? (
                      <div className="no-history">{t('ticketDetail.noHistory', 'Keine Historie vorhanden')}</div>
                    ) : (
                        sliced.map(renderHistoryItem)
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {error && <div className="error-message">{error}</div>}
              {aiError && <div className="error-message">{aiError}</div>}
              {loading ? (
                <div className="loading">{t('ticketDetail.loadingHistory', 'Lade Historie...')}</div>
              ) : aiSummary ? (
                <div className="ai-summary-view">
                  <div className="ai-summary-header">
                    <strong>{t('ticketDetail.aiSummary', 'KI-Zusammenfassung')}</strong>
                    <button className="btn btn-secondary btn-sm" onClick={handleClearAiSummary} disabled={aiSummarizing} style={{ marginLeft: 8 }}>
                      {t('ticketDetail.backToHistory', 'Zur Historie')}
                    </button>
                  </div>
                  <div className="ai-summary-content">
                    <ReactMarkdown>{aiSummary}</ReactMarkdown>
                  </div>
                </div>
              ) : (
                <div className="history-list" ref={setHistoryListEl}>
                  {(() => {
                    const filteredHistory = history.filter(item => item.type !== 'system');
                    const sliced = filteredHistory.slice(0, visibleHistoryCount);
                    return sliced.length === 0 ? (
                    <div className="no-history">{t('ticketDetail.noHistory', 'Keine Historie vorhanden')}</div>
                  ) : (
                      sliced.map(renderHistoryItem)
                    );
                  })()}
                </div>
              )}
            </>
          )}
        </div>

        {isDragOver && (
          <div className="drag-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'var(--color-backdrop-30)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '20px',
            pointerEvents: 'none'
          }}>
            {t('ticketDetail.filesDragOverlay', 'Dateien hierher ziehen, um sie anzuhängen')}
          </div>
        )}

        {/* EMAIL DETAIL MODAL - Rendered outside using Portal */}
        {showEmailDetail && createPortal(
          <EmailDetail 
            email={selectedEmail}
            onClose={handleCloseEmailDetail}
            onEmailUpdate={onTicketUpdate}
          />,
          document.body
        )}

        {/* TEAMS MESSAGE DETAIL MODAL - Rendered outside using Portal */}
        {showTeamsMessageDetail && createPortal(
          <TeamsMessageDetail 
            message={selectedTeamsMessage}
            onClose={handleCloseTeamsMessageDetail}
          />,
          document.body
        )}

        {/* #123: Ähnliche Vorfälle (KI-Suche) */}
        <TicketAiSearchModal
          ticket={currentTicket}
          open={showAiSearchModal}
          onClose={() => setShowAiSearchModal(false)}
          onAdopted={() => { loadTicketHistory(); }}
          onOpenTicket={openStackedTicket}
          lockClose={!!stackedTicket || stackedLoading}
        />

        {/* #231c: gestapeltes Ziel-Ticket über dem KI-Modal (Wrapper liegt über --z-modal) */}
        {stackedTicket && createPortal(
          <div style={{ position: 'fixed', inset: 0, zIndex: 10001 }}>
            <TicketDetail
              ticket={stackedTicket}
              onClose={(hasChanges = false) => {
                setStackedTicket(null);
                if (hasChanges) {
                  try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'stackedTicketDetailClosed', hasChanges } })); } catch (_) {}
                }
              }}
              onTicketUpdate={() => {
                try { window.dispatchEvent(new CustomEvent('ticketsUpdated', { detail: { reason: 'stackedTicketDetailUpdated' } })); } catch (_) {}
              }}
              isMobileView={isMobileView}
            />
          </div>,
          document.body
        )}

        <KommentarDialoge kommentare={kommentare} />

        <AbschlussModal abschluss={abschluss} currentTicket={currentTicket} getCurrentUserEmail={getCurrentUserEmail} />

        <UebergabeModal uebergabe={uebergabe} currentTicket={currentTicket} availableEmails={availableEmails} />

        {showVorgangDialog && (
          <VorgangDialog ticket={currentTicket} onClose={() => setShowVorgangDialog(false)} onGespeichert={nachVorgangGespeichert} />
        )}

        <AnhangDialoge anhaenge={anhaenge} formatFileSize={formatFileSize} />
        {mailAnhangViewer.viewer}

        {/* EDIT TICKET MODAL */}
        {showEditModal && (
        <Modal
          open
          onClose={() => setShowEditModal(false)}
          title={<><Edit3 size={24} /><h3>{t('ticketDetail.editTicketTitle', 'Ticket bearbeiten')}</h3><CalenderMarkerHeaderSelect value={editFormData.calenderMarker} onChange={(value) => setEditFormData({...editFormData, calenderMarker: value})} /></>}
          ariaLabel={t('ticketDetail.editTicketTitle', 'Ticket bearbeiten')}
          className="edit-ticket-modal"
          maxWidth="700px"
          footer={(
            // #622: in der Fußzeile des Modals, damit beide Knöpfe ohne Scrollen
            // sichtbar sind; Speichern löst das Formular über das form-Attribut aus
            <>
              <button type="button" onClick={() => setShowEditModal(false)} className="btn btn-secondary">
                {t('common.cancel', 'Abbrechen')}
              </button>
              <button type="submit" form="edit-ticket-form" disabled={updating} className="btn btn-primary">
                <Edit3 size={16} />
                {updating ? t('common.saving', 'Speichere...') : t('common.save', 'Speichern')}
              </button>
            </>
          )}
        >
              
              <form id="edit-ticket-form" onSubmit={handleSaveTicket} className="edit-ticket-form">
                <div className="form-body">
                  {/* #86/#89: Kunde über dem Titel; #105: Ansprechpartner daneben */}
                  <TicketCustomerField
                    value={editFormData.customerId}
                    onChange={(customerId) => setEditFormData(prev => ({...prev, customerId}))}
                    contactValue={editFormData.contactId}
                    onContactChange={(contactId) => setEditFormData(prev => ({...prev, contactId}))}
                    inputId="edit-customer"
                  />
                  <div className="form-group">
                    <label htmlFor="edit-title">{t('dashboard.field.title', 'Titel')} <span className="required">*</span></label>
                    <input
                      id="edit-title"
                      type="text"
                      value={editFormData.title}
                      onChange={(e) => setEditFormData({...editFormData, title: e.target.value})}
                      disabled={isProjectTicket}
                      required
                      placeholder={t('dashboard.placeholder.title', 'Ticket-Titel eingeben...')}
                      className="form-input"
                    />
                    {isProjectTicket && (
                      <div className="field-hint">
                        {t('ticketDetail.projectGeneratedTitle', 'Vorgangsgenerierter Titel')}
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="edit-description">{t('ticketDetail.description', 'Beschreibung')}</label>
                    <textarea
                      id="edit-description"
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                      placeholder={t('dashboard.placeholder.description', 'Ticket-Beschreibung eingeben...')}
                      rows="4"
                      className="form-textarea"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="edit-status">{t('ticketDetail.status', 'Status')}</label>
                      <div id="edit-status" className="status-badge" style={{ display: 'inline-block', padding: '6px 10px', borderRadius: '12px', backgroundColor: getStatusColor(editFormData.status), color: 'white', fontWeight: 'bold' }}>
                        {translateStatus(editFormData.status)}
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="edit-priority">{t('ticketDetail.priority', 'Priorität')}</label>
                      <select
                        id="edit-priority"
                        value={editFormData.priority}
                        onChange={(e) => setEditFormData({...editFormData, priority: e.target.value})}
                        className="form-select"
                      >
                        <option value="niedrig">{t('ticket.priority.low', 'Niedrig')}</option>
                        <option value="normal">{t('ticket.priority.normal', 'Normal')}</option>
                        <option value="hoch">{t('ticket.priority.high', 'Hoch')}</option>
                        <option value="kritisch">{t('ticket.priority.critical', 'Kritisch')}</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="edit-category">{t('dashboard.field.category', 'Kategorie')}</label>
                    <input
                      id="edit-category"
                      type="text"
                      value={editFormData.category}
                      onChange={(e) => setEditFormData({...editFormData, category: e.target.value})}
                      placeholder={t('dashboard.placeholder.category', 'z.B. Support, Bug, Feature')}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="edit-assigned-to">{t('dashboard.field.assignedTo', 'Zugewiesen an')}</label>
                    <select
                      id="edit-assigned-to"
                      value={editFormData.assignedTo}
                      onChange={(e) => setEditFormData({...editFormData, assignedTo: e.target.value})}
                      disabled={isProjectTicket}
                      className="form-select"
                    >
                      <option value="">{t('dashboard.unassigned', 'Nicht zugewiesen')}</option>
                      {availableEmails.map(email => (
                        <option key={email} value={email}>{email}</option>
                      ))}
                    </select>
                  </div>
                  

                </div>
              </form>
        </Modal>
        )}

        </div>
      </div>
    </div>
  );
};

export default TicketDetail; 